/**
 * PASSO 2 DO ASSISTENTE — de onde vem a base, e com que procedência.
 *
 * Antes, este passo era uma caixa de texto e um seletor "fria / com opt-in".
 * A tela mandava `origem` para o servidor, que esperava `loteId`, e toda
 * importação morria em 400. Do lado de quem usava: "erro ao puxar contatos".
 *
 * O `loteId` não é burocracia acidental. `crm.lote` exige declarante, data e
 * meio de obtenção, base legal, natureza dos titulares e o hash do arquivo
 * para o lote sair de `inutilizavel` — e contato de lote não liberado nunca
 * vira alvo. A base desta operação é MISTA: parte própria com opt-in, parte
 * fria sem. Sem declarar por lote, não há como segmentar a trilha de envio
 * nem responder "de onde veio este contato" quando alguém perguntar.
 *
 * O ARQUIVO SOBE INTEIRO, E ISSO É DE PROPÓSITO. Hash calculado no navegador
 * é hash que o navegador escolheu — não serve de prova. O servidor recebe o
 * arquivo, calcula o sha256, retém o cru e só então lê as linhas.
 *
 * Lote é reaproveitável: declara-se uma vez, usa-se em várias campanhas.
 */
import { useCallback, useMemo, useState } from "react";
import { api } from "../api.js";
import { irPara } from "../Casca.js";
import { Aviso, Botao, Campo, Painel, Selo, classeEntrada } from "../ui/base.js";
import { Grade, Leitura, useCarga } from "../ui/comercial.js";

export interface ResumoBase {
  total: number;
  aceitos: number;
  suprimidos: Array<{ motivo: string; quantidade: number }>;
}

interface Lote {
  id: string;
  rotulo: string;
  origem: string;
  naturezaTitulares: string;
  estado: string;
  obtidoEm: string | null;
  criadoEm: string;
  contatos: number;
  declarante: string | null;
}

/**
 * As origens do enum `crm.origem_lote`, com o que cada uma implica.
 *
 * `exigeCuidado` marca as que não têm consentimento do titular. Não bloqueia
 * — a operação é legítima e a base é mista —, mas muda o aviso na tela: os
 * dois números perdidos nesta operação caíram em disparo frio, e o segundo
 * com a cadência já corrigida.
 */
const ORIGENS: ReadonlyArray<{
  id: string;
  nome: string;
  desc: string;
  exigeCuidado: boolean;
  baseLegalSugerida: string;
}> = [
  {
    id: "base_propria_carteira",
    nome: "Carteira própria",
    desc: "cliente ou ex-cliente da própria empresa",
    exigeCuidado: false,
    baseLegalSugerida: "Legítimo interesse — relação comercial preexistente",
  },
  {
    id: "opt_in_formulario",
    nome: "Opt-in em formulário",
    desc: "o titular preencheu e consentiu",
    exigeCuidado: false,
    baseLegalSugerida: "Consentimento do titular",
  },
  {
    id: "evento_ou_feira",
    nome: "Evento ou feira",
    desc: "cartão ou cadastro entregue presencialmente",
    exigeCuidado: false,
    baseLegalSugerida: "Consentimento coletado presencialmente",
  },
  {
    id: "indicacao",
    nome: "Indicação",
    desc: "terceiro passou o contato",
    exigeCuidado: true,
    baseLegalSugerida: "Legítimo interesse — indicação de terceiro",
  },
  {
    id: "lista_publica",
    nome: "Lista pública",
    desc: "dado publicamente acessível",
    exigeCuidado: true,
    baseLegalSugerida: "Legítimo interesse — dado tornado público pelo titular",
  },
  {
    id: "aquisicao_terceiro",
    nome: "Lista adquirida",
    desc: "comprada de terceiro — precisa de direito de uso documentado",
    exigeCuidado: true,
    baseLegalSugerida: "Legítimo interesse — contrato de cessão com o fornecedor",
  },
];

const NATUREZAS: ReadonlyArray<{ id: string; nome: string; desc: string }> = [
  { id: "pj", nome: "Pessoa jurídica", desc: "CNPJ — o caso desta operação" },
  { id: "pf", nome: "Pessoa física", desc: "CPF — regime mais estrito" },
  { id: "misto", nome: "Misto", desc: "os dois na mesma planilha" },
];

const ROTULO_ORIGEM: Record<string, string> = Object.fromEntries(
  ORIGENS.map((o) => [o.id, o.nome]),
);

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Lê o arquivo como base64, sem o prefixo `data:` que o FileReader acrescenta. */
function lerComoBase64(f: File): Promise<string> {
  return new Promise((ok, falha) => {
    const r = new FileReader();
    r.onload = () => {
      const v = String(r.result ?? "");
      const i = v.indexOf(",");
      ok(i >= 0 ? v.slice(i + 1) : v);
    };
    r.onerror = () => falha(new Error("Não consegui ler o arquivo no navegador."));
    r.readAsDataURL(f);
  });
}

export function PassoDaBase({
  campanhaId,
  baseEnviada,
  resumoBase,
  aoGarantirCampanha,
  aoImportar,
}: {
  campanhaId: string | null;
  baseEnviada: boolean;
  resumoBase: ResumoBase | null;
  aoGarantirCampanha: () => Promise<string>;
  aoImportar: (r: ResumoBase | null) => void;
}): JSX.Element {
  const { estado: cargaLotes, recarregar } = useCarga(
    useCallback(() => api.lotes(), []),
  );
  // A política de descanso é pré-requisito silencioso da importação: sem ela,
  // `compliance.pode_contatar` suprime 100% da base com motivo "sem_politica".
  // O aviso vem ANTES de importar, porque descobrir depois custa a base
  // inteira suprimida e uma hora entendendo por quê.
  const { estado: cargaConfig } = useCarga(useCallback(() => api.config(), []));
  const semPolitica =
    cargaConfig.fase === "pronto" && cargaConfig.dados.descanso?.definida === false;
  const lotes: Lote[] =
    cargaLotes.fase === "pronto" ? (cargaLotes.dados.itens as Lote[]) : [];

  const [modo, setModo] = useState<"novo" | "existente">("novo");
  const [loteEscolhido, setLoteEscolhido] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState(false);

  // Declaração
  const [rotulo, setRotulo] = useState("");
  const [origem, setOrigem] = useState(ORIGENS[0]!.id);
  const [obtidoEm, setObtidoEm] = useState(hoje);
  const [meioObtencao, setMeioObtencao] = useState("");
  const [baseLegal, setBaseLegal] = useState(ORIGENS[0]!.baseLegalSugerida);
  const [natureza, setNatureza] = useState("pj");

  // Arquivo ou colagem
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [colagem, setColagem] = useState("");

  const origemAtual = ORIGENS.find((o) => o.id === origem) ?? ORIGENS[0]!;

  const problema = useMemo(() => {
    if (modo === "existente") {
      return loteEscolhido === "" ? "Escolha um lote já declarado." : null;
    }
    if (rotulo.trim().length < 3) return "Dê um rótulo ao lote (mínimo 3 caracteres).";
    if (meioObtencao.trim().length < 3) return "Descreva como a base foi obtida.";
    if (baseLegal.trim().length < 3) return "Declare a base legal.";
    if (obtidoEm > hoje()) return "A data de obtenção está no futuro.";
    if (!arquivo && colagem.trim().length === 0) {
      return "Escolha uma planilha ou cole os contatos.";
    }
    return null;
  }, [modo, loteEscolhido, rotulo, meioObtencao, baseLegal, obtidoEm, arquivo, colagem]);

  async function enviar(): Promise<void> {
    if (problema) {
      setErro(problema);
      return;
    }
    setOcupado(true);
    setErro(null);
    setAvisos([]);
    try {
      const id = campanhaId ?? (await aoGarantirCampanha());

      let loteId = loteEscolhido;
      let contatos: Array<{ telefone: string; nome: string | null }> = [];

      if (modo === "novo") {
        const ehXlsx = arquivo?.name.toLowerCase().endsWith(".xlsx") ?? false;
        const conteudoBase64 = arquivo
          ? await lerComoBase64(arquivo)
          : btoa(unescape(encodeURIComponent(colagem)));

        const lote = await api.criarLote({
          rotulo: rotulo.trim(),
          origem,
          obtidoEm,
          meioObtencao: meioObtencao.trim(),
          baseLegal: baseLegal.trim(),
          naturezaTitulares: natureza,
          arquivo: {
            nome: arquivo?.name ?? `colagem-${obtidoEm}.txt`,
            tipo: arquivo ? (ehXlsx ? "xlsx" : "csv") : "colado",
            conteudoBase64,
          },
        });
        loteId = lote.id;
        contatos = lote.contatos;
        setAvisos(lote.avisos ?? []);
        recarregar();
      } else {
        // Lote já declarado: os contatos dele já estão no CRM. A importação
        // materializa os alvos desta campanha a partir do lote.
        const escolhido = lotes.find((l) => l.id === loteEscolhido);
        if (!escolhido) throw new Error("Lote não encontrado. Recarregue a lista.");
        const c = await api.contatosDoLote(loteEscolhido);
        contatos = c.contatos;
        if (contatos.length === 0) {
          throw new Error(`O lote "${escolhido.rotulo}" não tem contatos utilizáveis.`);
        }
      }

      const r = await api.importarBase(id, {
        loteId,
        contatos,
        autorizaWhatsapp: true,
      });
      aoImportar(lerResumo(r));
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "A base não foi aceita.");
    } finally {
      setOcupado(false);
    }
  }

  if (baseEnviada) {
    return (
      <div className="grid gap-4">
        <Aviso tom="ok">
          Base gravada nesta campanha. Este assistente não a envia de novo: o
          contrato não define se um segundo envio substitui ou acrescenta, e
          contato duplicado quebraria a conta do funil.
        </Aviso>
        {resumoBase && <Recorte r={resumoBase} />}
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {erro && <Aviso tom="crit">{erro}</Aviso>}

      {semPolitica && (
        <Aviso tom="crit">
          <div className="grid gap-2.5">
            <span>
              <strong>Nenhuma política de descanso está salva.</strong> Sem ela,
              a conferência da base suprime <strong>todos</strong> os contatos —
              a importação parece funcionar, o funil fica inteiro em “ignorado” e
              nada é enviado. É falha fechada de propósito; o que faltava era o
              aviso.
            </span>
            <div>
              <Botao tipo="principal" pequeno onClick={() => irPara("config")}>
                Definir a política de descanso
              </Botao>
            </div>
          </div>
        </Aviso>
      )}

      <div className="flex gap-1.5 flex-wrap">
        <Botao
          tipo={modo === "novo" ? "principal" : "fantasma"}
          pequeno
          onClick={() => setModo("novo")}
        >
          Importar planilha ou colar
        </Botao>
        <Botao
          tipo={modo === "existente" ? "principal" : "fantasma"}
          pequeno
          desabilitado={lotes.length === 0}
          {...(lotes.length === 0 ? { titulo: "Nenhum lote declarado ainda" } : {})}
          onClick={() => setModo("existente")}
        >
          Usar lote já declarado ({lotes.length})
        </Botao>
      </div>

      {modo === "existente" ? (
        <Painel titulo="Lote já declarado" dica="Declara-se uma vez; serve a várias campanhas">
          <Campo rotulo="Lote">
            <select
              className={classeEntrada}
              value={loteEscolhido}
              onChange={(e) => setLoteEscolhido(e.target.value)}
            >
              <option value="">Escolha…</option>
              {lotes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.rotulo} — {ROTULO_ORIGEM[l.origem] ?? l.origem} · {l.contatos} contato(s)
                </option>
              ))}
            </select>
          </Campo>
        </Painel>
      ) : (
        <>
          <Painel
            titulo="De onde veio esta base"
            dica="Declaração exigida pelo banco: sem ela o contato não vira alvo"
          >
            <div className="grid gap-4">
              <Campo rotulo="Rótulo do lote" dica="Como você vai reconhecê-lo depois">
                <input
                  className={classeEntrada}
                  value={rotulo}
                  placeholder="Mailing CNPJ Vivo PME — agosto/2026"
                  onChange={(e) => setRotulo(e.target.value)}
                />
              </Campo>

              <Grade colunas={2}>
                <Campo rotulo="Origem">
                  <select
                    className={classeEntrada}
                    value={origem}
                    onChange={(e) => {
                      setOrigem(e.target.value);
                      const o = ORIGENS.find((x) => x.id === e.target.value);
                      if (o) setBaseLegal(o.baseLegalSugerida);
                    }}
                  >
                    {ORIGENS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.nome} — {o.desc}
                      </option>
                    ))}
                  </select>
                </Campo>

                <Campo rotulo="Natureza dos titulares">
                  <select
                    className={classeEntrada}
                    value={natureza}
                    onChange={(e) => setNatureza(e.target.value)}
                  >
                    {NATUREZAS.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.nome} — {n.desc}
                      </option>
                    ))}
                  </select>
                </Campo>

                <Campo rotulo="Obtida em">
                  <input
                    type="date"
                    className={classeEntrada}
                    value={obtidoEm}
                    max={hoje()}
                    onChange={(e) => setObtidoEm(e.target.value)}
                  />
                </Campo>

                <Campo rotulo="Meio de obtenção" dica="Como o arquivo chegou até você">
                  <input
                    className={classeEntrada}
                    value={meioObtencao}
                    placeholder="Exportação do CRM da CO Telecom"
                    onChange={(e) => setMeioObtencao(e.target.value)}
                  />
                </Campo>
              </Grade>

              <Campo rotulo="Base legal" dica="Sugerida pela origem; ajuste se o caso for outro">
                <input
                  className={classeEntrada}
                  value={baseLegal}
                  onChange={(e) => setBaseLegal(e.target.value)}
                />
              </Campo>

              <Aviso tom={origemAtual.exigeCuidado ? "warn" : "cool"}>
                {origemAtual.exigeCuidado ? (
                  <>
                    <strong>Base sem consentimento do titular.</strong> É legítimo,
                    e é a maior parte desta operação — mas os dois números
                    perdidos aqui caíram em disparo frio, e o segundo já com a
                    cadência corrigida. Cadência ajuda; o que decide é o texto.
                    Confira as variações do kit antes de aprovar.
                  </>
                ) : (
                  <>
                    <strong>Base com relação prévia ou consentimento.</strong> A
                    política de descanso continua valendo mesmo aqui: opt-in
                    autoriza falar, não autoriza falar todo dia.
                  </>
                )}
              </Aviso>
            </div>
          </Painel>

          <Painel
            titulo="Os contatos"
            dica="Planilha do Excel, CSV, ou colado — o arquivo original fica retido"
          >
            <div className="grid gap-4">
              <Campo
                rotulo="Planilha"
                dica="Aceita .xlsx e .csv. A coluna de telefone é encontrada pelo cabeçalho."
              >
                <input
                  type="file"
                  accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="block w-full text-[13px] text-tinta-2 file:mr-3 file:rounded-lg file:border-0 file:bg-superficie-3 file:px-3 file:py-1.5 file:text-[12.5px] file:text-tinta cursor-pointer"
                  onChange={(e) => {
                    setArquivo(e.target.files?.[0] ?? null);
                    setColagem("");
                  }}
                />
              </Campo>

              {arquivo ? (
                <div className="text-[12.5px] text-tinta-2">
                  <Selo tom="ok">{arquivo.name}</Selo>{" "}
                  {(arquivo.size / 1024).toFixed(0)} KB — o servidor lê as linhas
                  e guarda o arquivo como prova do que foi carregado.
                </div>
              ) : (
                <Campo
                  rotulo="Ou cole os contatos"
                  dica="Um por linha: telefone, ou telefone;nome"
                >
                  <textarea
                    className={`${classeEntrada} font-mono min-h-[140px] leading-relaxed`}
                    value={colagem}
                    onChange={(e) => setColagem(e.target.value)}
                    placeholder={"67992168673;Padaria do Zé\n6733214455"}
                  />
                </Campo>
              )}

              <p className="text-[12px] text-tinta-3 leading-relaxed">
                Formate a coluna de telefone como <strong>texto</strong> no
                Excel. Como número, a célula perde o zero à esquerda e o DDD vai
                junto — é a causa mais comum de linha descartada.
              </p>
            </div>
          </Painel>
        </>
      )}

      {avisos.length > 0 && (
        <Aviso tom="warn">
          <ul className="grid gap-1">
            {avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Aviso>
      )}

      <div>
        <Botao
          tipo="principal"
          desabilitado={ocupado || problema !== null}
          onClick={() => void enviar()}
        >
          {ocupado ? "Conferindo…" : "Conferir base e continuar"}
        </Botao>
        {problema && (
          <span className="ml-3 text-[12px] text-tinta-3">{problema}</span>
        )}
      </div>
    </div>
  );
}

function Recorte({ r }: { r: ResumoBase }): JSX.Element {
  return (
    <Painel titulo="Recorte depois da supressão" dica="O que entrou e o que ficou de fora, com o motivo">
      <Grade colunas={2}>
        <Leitura rotulo="Enviados para conferência">{r.total}</Leitura>
        <Leitura rotulo="Entraram na base">{r.aceitos}</Leitura>
      </Grade>
      {r.suprimidos.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
            Suprimidos por motivo
          </div>
          <ul className="grid gap-1.5">
            {r.suprimidos.map((s) => (
              <li key={s.motivo} className="flex items-baseline gap-3 text-[13px]">
                <strong className="tabular-nums">{s.quantidade}</strong>
                <span className="text-tinta-2">{s.motivo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Painel>
  );
}

/**
 * Lê a resposta da importação.
 *
 * O servidor devolve `recebidos`, `alvosCriados`, `suprimidos` e `porMotivo` —
 * não `total`/`aceitos`. A primeira versão desta função procurava os nomes
 * errados, devolvia null, e a tela não mostrava recorte NENHUM depois de
 * importar. Descoberto percorrendo a jornada como operador, não por teste.
 */
export function lerResumo(v: unknown): ResumoBase | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const num = (k: string): number | null =>
    typeof o[k] === "number" ? (o[k] as number) : null;

  const total = num("recebidos") ?? num("total");
  // O que entrou de fato na campanha é o alvo criado, não o contato aceito:
  // contato pode já existir no CRM e ainda assim ser suprimido aqui.
  const aceitos = num("alvosCriados") ?? num("aceitos");
  if (total === null || aceitos === null) return null;

  const brutos = Array.isArray(o["porMotivo"])
    ? o["porMotivo"]
    : Array.isArray(o["suprimidos"])
      ? o["suprimidos"]
      : [];
  const suprimidos = brutos.flatMap((s) => {
    if (typeof s !== "object" || s === null) return [];
    const x = s as Record<string, unknown>;
    const motivo =
      typeof x["rotulo"] === "string"
        ? x["rotulo"]
        : typeof x["motivo"] === "string"
          ? x["motivo"]
          : null;
    const quantidade = x["quantidade"];
    if (motivo === null || typeof quantidade !== "number") return [];
    return [{ motivo, quantidade }];
  });
  return { total, aceitos, suprimidos };
}
